import * as React from 'react';

import { atom, useAtomValue, useSetAtom } from 'jotai';
import { LucideLoader2 } from 'lucide-react';

import { cn } from '../../../lib/stylingUtils';
import { MaybeWrap } from '../../utilities';
import { Col } from '../containers';

/** Spinner size variants */
export type LoadingSize = 'sm' | 'default' | 'lg';

const $isShowGlobalLoading = atom(false);

export type LoadingProps = React.HTMLAttributes<HTMLDivElement> & {
  isGlobal?: boolean;
  isBoxed?: boolean;
  /** Whether the loading state is active */
  isLoading?: boolean;
  /** Content to display when not loading */
  children?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show "Loading..." text label next to spinner */
  showLabel?: boolean;

  // Props for enhanced functionality
  /** Show semi-transparent overlay over children */
  overlay?: boolean;
  /** Overlay opacity (0-100) */
  overlayOpacity?: number;
  /** Skeleton placeholder for initial loads */
  skeleton?: React.ReactNode;
  /** Whether to show skeleton instead of spinner */
  showSkeleton?: boolean;
};

type LoadingIndicatorProps = {
  showLabel: boolean;
  isBoxed?: boolean;
  isGlobal?: boolean;
};

/**
 * Loading indicator component (spinner + optional label)
 * Layout matches Figma: flex row with 8px gap, text on right
 */
// eslint-disable-next-line custom/require-component-classname
const LoadingIndicator = ({
  showLabel,
  isBoxed,
  isGlobal,
}: LoadingIndicatorProps) => {
  const isShowGlobalLoading = useAtomValue($isShowGlobalLoading);

  return (
    <MaybeWrap
      condition={!!isBoxed}
      wrapper={(children) => (
        <div
          className={cn(
            'light animate-in rounded-lg bg-abs-white p-4 shadow outline-4 outline-foreground/5 fade-in-0',
            !isGlobal && isShowGlobalLoading && 'opacity-0',
          )}
        >
          {children}
        </div>
      )}
    >
      <Col
        xCenter
        yCenter
        gap2
        className={cn(
          '@LoadingIndicator',
          !isGlobal && isShowGlobalLoading && 'opacity-0',
        )}
      >
        <LucideLoader2
          className={cn(
            'size-8 animate-spin opacity-50',
            !showLabel && 'size-10 stroke-[1.5]',
          )}
        />
        {showLabel && <span className="text-sm text-neutral/60">Loading</span>}
      </Col>
    </MaybeWrap>
  );
};

/**
 * Loading component with multiple display modes
 *
 * Modes:
 * 1. Default: Centered spinner in full container
 * 2. Overlay: Semi-transparent overlay with spinner over children
 * 3. Skeleton: Custom skeleton placeholder
 *
 * @example
 * ```tsx
 * // Default mode - centered spinner
 * <Loading isLoading={isLoading}>
 *   <Content />
 * </Loading>
 *
 * // Overlay mode - content visible with overlay
 * <Loading isLoading={isLoading} overlay>
 *   <Content />
 * </Loading>
 *
 * // Skeleton mode - show skeleton on initial load
 * <Loading isLoading={isLoading} showSkeleton skeleton={<MySkeleton />}>
 *   <Content />
 * </Loading>
 *
 * // With size and label control
 * <Loading isLoading={isLoading} size="lg" showLabel={false} />
 * ```
 */
export const Loading = ({
  isGlobal,
  isBoxed,
  className,
  isLoading,
  children,
  showLabel = false,
  overlay = false,
  skeleton,
  showSkeleton = false,
  ...props
}: LoadingProps) => {
  const setIsShowGlobalLoading = useSetAtom($isShowGlobalLoading);

  React.useEffect(() => {
    if (isGlobal) {
      setIsShowGlobalLoading(true);

      return () => {
        setIsShowGlobalLoading(false);
      };
    }
  }, [isGlobal, setIsShowGlobalLoading]);

  // Show skeleton if specified and loading
  if (isLoading && showSkeleton && skeleton) {
    return skeleton;
  }

  // Show overlay if specified
  if (isLoading && overlay) {
    return (
      <div className={cn('relative')}>
        {/* Keep children visible underneath */}
        {children}

        <div
          className={cn(
            'bg-background/50 absolute inset-0 z-10 flex items-center justify-center',
            className,
          )}
          {...props}
        >
          <LoadingIndicator
            showLabel={showLabel}
            isBoxed={isBoxed}
            isGlobal={isGlobal}
          />
        </div>
      </div>
    );
  }

  // Original behavior for non-overlay mode
  if (isLoading) {
    return (
      <div
        className={cn('flex size-full items-center justify-center', className)}
        {...props}
      >
        <LoadingIndicator
          showLabel={showLabel}
          isBoxed={isBoxed}
          isGlobal={isGlobal}
        />
      </div>
    );
  }

  return children;
};
